import { Button, Drawer, Input, Popconfirm, Skeleton, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { LuSearchX, LuStar } from 'react-icons/lu'
import { MdOutlineErrorOutline } from 'react-icons/md'
import { VscClearAll } from 'react-icons/vsc'

import { useNotification } from '@/Core/Hooks/useNotification'
import { GetResourceTask } from '@/Core/Tasks/Resources/GetResourceTask'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { Hint } from '@/Ship/Components/Hint'
import { FAVORITE_RESOURCES } from '@/Ship/Configs/FavoriteResourcesConfig'
import { classNames } from '@/Ship/Helpers/classNames'
import { favoritesManager } from '@/Ship/Stores/FavoritesManager'

import { matchesFavorite } from '../Helpers/matchesFavorite'
import { FavoritesDrawerStore } from '../Stores/FavoritesDrawerStore'
import { FavoritesListItem } from './FavoritesListItem'

import type { Favorite } from '@/Ship/Stores/FavoritesManager'

import styles from './Favorites.module.css'

/** Ширина шухляди — та сама, що в сповіщень: дві шухляди шапки однакові. */
const DRAWER_WIDTH = 560

interface Group {
    key: string
    label: string
    items: Favorite[]
}

/** Місце групи — як у бічному меню; розділ, якого в переліку немає, — у кінці. */
const rank = (entityCode: string): number => {
    const index = FAVORITE_RESOURCES.indexOf(entityCode)

    return index === -1 ? FAVORITE_RESOURCES.length : index
}

/**
 * Підпис групи — як пункт меню («Співробітники»), а не назва картки
 * («Співробітник»): поруч стоять інші розділи меню у множині, і однина серед
 * них виглядала б випадковою.
 */
const sectionTitle = (entityCode: string): string => {
    const resource = GetResourceTask(entityCode)
    const pageTitle: unknown = resource?.['pageConfig']?.title

    return typeof pageTitle === 'string' ? pageTitle : (resource?.title ?? 'Інше')
}

/** Записи одного розділу — в один блок. Усередині — свіжіші позначки зверху, як віддає бек. */
const groupByResource = (items: Favorite[]): Group[] => {
    const groups = new Map<string, Group>()

    for (const item of items) {
        let group = groups.get(item.entityCode)

        if (!group) {
            group = { key: item.entityCode, label: sectionTitle(item.entityCode), items: [] }
            groups.set(item.entityCode, group)
        }

        group.items.push(item)
    }

    return Array.from(groups.values()).sort((a, b) => rank(a.key) - rank(b.key))
}

/**
 * Обране користувача — шухляда із зірочки в шапці.
 *
 * Клік по запису відкриває його картку поверх шухляди, а шухляда лишається:
 * закривши картку, людина повертається до списку. Зірочка, знята в картці,
 * прибирає запис і звідси — обидві читають один стор.
 *
 * Від легасі (ADR 029): пошук за назвою й коментарем, коментар другим рядком,
 * кошик «Видалити» і «Видалити все» — з підтвердженням, якого в легасі не було.
 */
export const FavoritesDrawer = observer(() => {
    const { showError } = useNotification()
    const [isFailed, setIsFailed] = useState(false)
    const [query, setQuery] = useState('')

    const isOpen = FavoritesDrawerStore.isOpen
    const items = favoritesManager.items
    const found = useMemo(() => items.filter((item) => matchesFavorite(item, query)), [items, query])
    const groups = useMemo(() => groupByResource(found), [found])

    const load = () => {
        setIsFailed(false)
        favoritesManager.load().catch((error: unknown) => {
            console.warn('FavoritesDrawer: не вдалося прочитати обране', error)
            setIsFailed(true)
        })
    }

    // Щоразу при відкритті — свіжий список: позначати могли з іншої вкладки
    useEffect(() => {
        if (isOpen) load()
    }, [isOpen])

    const open = (item: Favorite) => {
        OpenResourceCardTask({ resource: item.entityCode, primaryFieldValue: item.entityUuid })
    }

    const remove = (item: Favorite) => {
        favoritesManager.remove(item).catch(() => showError('Не вдалося видалити з обраного'))
    }

    const removeAll = () => {
        favoritesManager.removeAll().catch(() => showError('Не вдалося видалити обране'))
    }

    const isEmpty = items.length === 0
    const isNothingFound = !isEmpty && found.length === 0

    return (
        <Drawer
            size={Math.min(DRAWER_WIDTH, window.innerWidth)}
            title="Обране"
            open={isOpen}
            onClose={() => FavoritesDrawerStore.setIsOpen(false)}
            styles={{ body: { padding: 0 } }}
            extra={
                !isEmpty && (
                    <Popconfirm
                        title="Ви дійсно хочете видалити?"
                        okText="Так"
                        cancelText="Ні"
                        placement="bottomRight"
                        onConfirm={removeAll}
                    >
                        <Tooltip title="Видалити все" placement="bottomRight">
                            <Button
                                type="text"
                                aria-label="Видалити все"
                                icon={<VscClearAll style={{ fontSize: 20, color: 'var(--ant-color-error)' }} />}
                            />
                        </Tooltip>
                    </Popconfirm>
                )
            }
        >
            <div className={classNames(styles['body'])}>
                <Hint id="shell.favorites.privacy">Обране й коментарі до нього бачите лише ви.</Hint>

                {!isEmpty && (
                    <Input.Search
                        className={classNames(styles['search'])}
                        placeholder="Пошук..."
                        allowClear
                        value={query}
                        aria-label="Пошук в обраному"
                        onChange={(event) => setQuery(event.target.value)}
                    />
                )}

                {isNothingFound && (
                    <div className={classNames(styles['empty'])}>
                        <span className={classNames(styles['emptyIcon'])}>
                            <LuSearchX strokeWidth={1.75} />
                        </span>
                        <div className={classNames(styles['emptyTitle'])}>Нічого не знайдено...</div>
                    </div>
                )}

                {isEmpty && isFailed && (
                    <div className={classNames(styles['empty'])}>
                        <span className={classNames(styles['emptyIcon'])}>
                            <MdOutlineErrorOutline />
                        </span>
                        <div className={classNames(styles['emptyTitle'])}>Не вдалося завантажити обране</div>
                        <Button onClick={load}>Спробувати ще раз</Button>
                    </div>
                )}

                {isEmpty && !isFailed && !favoritesManager.isLoaded && (
                    <div className={classNames(styles['group'], styles['skeleton'])}>
                        {[0, 1, 2].map((index) => (
                            <Skeleton key={index} active avatar={{ size: 32 }} title={false} paragraph={{ rows: 1 }} />
                        ))}
                    </div>
                )}

                {isEmpty && !isFailed && favoritesManager.isLoaded && (
                    <div className={classNames(styles['empty'])}>
                        <span className={classNames(styles['emptyIcon'])}>
                            <LuStar strokeWidth={1.75} />
                        </span>
                        <div className={classNames(styles['emptyTitle'])}>Обраного ще немає</div>
                        <div className={classNames(styles['emptyText'])}>
                            Позначте зірочкою картку клієнта, перевізника чи співробітника — і вона з'явиться тут.
                        </div>
                    </div>
                )}

                {groups.map((group) => (
                    <section key={group.key} className={classNames(styles['section'])}>
                        <div className={classNames(styles['sectionLabel'])}>{group.label}</div>
                        <div className={classNames(styles['group'])}>
                            {group.items.map((item) => (
                                <FavoritesListItem
                                    key={`${item.entityCode}:${item.entityUuid}`}
                                    item={item}
                                    onOpen={open}
                                    onRemove={remove}
                                />
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </Drawer>
    )
})
